import React from 'react'

import { Thumbnail } from 'features/views/attributes/Thumbnail'
import { LayoutEditorImageControl } from 'features/views/LayoutEditor/controls/LayoutEditorImageControl'

import { Icon as V2Icon } from 'v2/ui'

import { Box } from 'ui/components/Box'
import { Button } from 'ui/components/Button'
import { Divider } from 'ui/components/Divider'
import { Icon } from 'ui/components/Icon'
import { IconName } from 'ui/components/Icon/Icon'
import { ScrollArea } from 'ui/components/ScrollArea'
import { Skeleton } from 'ui/components/Skeleton'
import { Body, Headline } from 'ui/components/Text'
import { Textarea } from 'ui/components/Textarea'
import { useResponsiveValue } from 'ui/styling/helpers/useResponsiveValue'
import { theme } from 'ui/styling/Theme.css'

import { useAppTemplateDetailDescriptionState } from './hooks/useAppTemplateDetailDescriptionState'
import { useAppTemplateDetailScreenshotState } from './hooks/useAppTemplateDetailScreenshotState'
import { AppTemplateIcon } from './AppTemplateIcon'
import { AppTemplateTagEditor } from './AppTemplateTagEditor'
import { AppTemplate, AppTemplateContentItem, AppTemplateContentSection } from './types'

import {
    AppTemplateDetailContentsSectionItemsStyles,
    AppTemplateDetailMetadataStyles,
    AppTemplateDetailScreenshotStyle,
    AppTemplateDetailScreenshotWrapperStyles,
    AppTemplateDetailStyles,
} from './AppTemplateDetail.css'

type AppTemplateDetailProps = {
    template: AppTemplate
    isEditable?: boolean
    isLoading?: boolean
    onChange?: (template: Partial<AppTemplate>) => void
}

export const AppTemplateDetail: React.FC<AppTemplateDetailProps> = ({
    template,
    isEditable,
    isLoading,
    onChange,
}) => {
    const isScrollable = useResponsiveValue({
        mobile: false,
        tablet: true,
    })

    const appTemplateIconSize = useResponsiveValue({
        mobile: 'xl' as const,
        tablet: '2xl' as const,
    })

    const metadata = (
        <Box flex column gap="xl" pointerEvents={isLoading ? 'none' : undefined}>
            <Skeleton style={{ width: 'max-content' }} isLoading={isLoading}>
                <AppTemplateIcon size={appTemplateIconSize} template={template} />
            </Skeleton>
            <Box flex column gap="m">
                <Skeleton isLoading={isLoading} isInline>
                    <Headline size="2xs">{template.name}</Headline>
                </Skeleton>
                <Skeleton isLoading={isLoading} isInline>
                    <AppTemplateDetailDescription
                        template={template}
                        isEditable={isEditable}
                        onChange={onChange}
                    />
                </Skeleton>
                {isEditable && (
                    <Skeleton isLoading={isLoading} style={{ width: 'max-content' }}>
                        <AppTemplateTagEditor template={template} onChange={onChange} mt="xs" />
                    </Skeleton>
                )}
            </Box>
            {!isLoading && template.contents.length > 0 && (
                <AppTemplateDetailContents sections={template.contents} />
            )}
        </Box>
    )

    return (
        <Box className={AppTemplateDetailStyles.styleFunction({})}>
            <Box className={AppTemplateDetailMetadataStyles.styleFunction({})}>
                {isScrollable ? (
                    <ScrollArea
                        direction="vertical"
                        rootProps={{
                            height: 'full',
                        }}
                    >
                        {metadata}
                    </ScrollArea>
                ) : (
                    metadata
                )}
            </Box>
            <AppTemplateDetailScreenshot
                template={template}
                isEditable={isEditable}
                onChange={onChange}
                isLoading={isLoading}
            />
        </Box>
    )
}

type AppTemplateDetailDescriptionProps = {
    template: AppTemplate
    onChange?: (template: Partial<AppTemplate>) => void
    isEditable?: boolean
}

const AppTemplateDetailDescription: React.FC<AppTemplateDetailDescriptionProps> = ({
    template,
    onChange,
    isEditable,
}) => {
    const { description, handleChange } = useAppTemplateDetailDescriptionState({
        template,
        onChange,
    })

    if (!isEditable && !template.description) {
        return null
    }

    if (isEditable) {
        return (
            <Textarea
                value={description}
                onChange={handleChange}
                autoSize
                placeholder="Add description..."
                style={{
                    width: '100%',
                    minHeight: 0,
                    alignItems: 'center',
                    padding: 0,
                    border: 'none',
                    boxShadow: 'none',
                    borderRadius: 0,
                }}
            />
        )
    }

    return (
        <Body size="m" color="textWeak">
            {template.description}
        </Body>
    )
}
type AppTemplateDetailContentsProps = {
    sections: AppTemplateContentSection[]
}

const AppTemplateDetailContents: React.FC<AppTemplateDetailContentsProps> = ({ sections }) => {
    return (
        <Box flex column gap="xl">
            {sections.map((section, idx) => (
                <React.Fragment key={idx}>
                    <AppTemplateDetailContentsSection section={section} />
                    {idx < sections.length - 1 && <Divider variant="strong" />}
                </React.Fragment>
            ))}
        </Box>
    )
}

// If the section has less than this many items, we'll display them in a single column.
const SINGLE_COLUMN_THRESHOLD = 4

type AppTemplateDetailContentsSectionProps = {
    section: AppTemplateContentSection
}

const AppTemplateDetailContentsSection: React.FC<AppTemplateDetailContentsSectionProps> = ({
    section,
}) => {
    const isSingleColumn = section.items.length < SINGLE_COLUMN_THRESHOLD

    return (
        <Box flex column gap="m">
            <Box flex column>
                <Box p="xs">
                    <Body
                        size="xs"
                        style={{ textTransform: 'uppercase' }}
                        color="textWeaker"
                        weight="bold"
                    >
                        {section.label}
                    </Body>
                </Box>
                {section.description && (
                    <Box p="xs">
                        <Body size="m" color="textWeak">
                            {section.description}
                        </Body>
                    </Box>
                )}
            </Box>
            {section.items.length > 0 && (
                <Box
                    className={AppTemplateDetailContentsSectionItemsStyles.styleFunction({
                        isSingleColumn,
                    })}
                >
                    {section.items.map((item, idx) => (
                        <AppTemplateDetailContentsItem item={item} key={idx} />
                    ))}
                </Box>
            )}
        </Box>
    )
}

type AppTemplateDetailContentsItemProps = {
    item: AppTemplateContentItem
}

const AppTemplateDetailContentsItem: React.FC<AppTemplateDetailContentsItemProps> = ({ item }) => {
    const iconFamily = item.icon?.family || 'hugeicons'

    return (
        <Box flex center gap="m" p="xs" minWidth={0} color="icon">
            {(iconFamily === 'legacy' || iconFamily === 'fa') && (
                <V2Icon
                    iconPack="far"
                    icon={item.icon?.name}
                    color="inherit"
                    fontSize={theme.icon.size.xs}
                    width={theme.icon.size.s}
                    height={theme.icon.size.s}
                    flexShrink={0}
                />
            )}
            {(iconFamily === 'hugeicons' || iconFamily === 'lucide') && (
                <Icon
                    family={iconFamily}
                    name={item.icon?.name as IconName<typeof iconFamily>}
                    size="s"
                    color="icon"
                    noShrink
                />
            )}
            <Box minWidth={0} trim color="text">
                <Body size="m" weight="medium">
                    {item.label}
                </Body>
            </Box>
        </Box>
    )
}

type AppTemplateDetailScreenshotProps = {
    template: AppTemplate
    isEditable?: boolean
    onChange?: (template: Partial<AppTemplate>) => void
    isLoading?: boolean
}

const AppTemplateDetailScreenshot: React.FC<AppTemplateDetailScreenshotProps> = ({
    template,
    isEditable,
    onChange,
    isLoading,
}) => {
    const {
        screenshotUrl,
        onUploadClick,
        showThumbnail,
        onRemoveScreenshot,
        thumbnailUrl,
        onChangeThumbnail,
    } = useAppTemplateDetailScreenshotState({
        template,
        onChange,
        isEditable,
    })

    return (
        <Box className={AppTemplateDetailScreenshotWrapperStyles.styleFunction({})}>
            {isEditable && !isLoading && (
                <Box position="absolute" bottom="xl" right="xl" style={{ zIndex: 99 }}>
                    <Box width="200px">
                        <LayoutEditorImageControl
                            placeholder="Upload thumbnail"
                            value={thumbnailUrl}
                            onChange={onChangeThumbnail}
                        />
                    </Box>
                </Box>
            )}
            <Skeleton isLoading={isLoading} width="full" height="full" isInline display="block">
                {isEditable && showThumbnail && (
                    <Box position="absolute" top="xl" right="xl" style={{ zIndex: 99 }}>
                        <Button
                            variant="secondary"
                            size="2xs"
                            aria-label="Remove image"
                            onClick={onRemoveScreenshot}
                            startIcon={{ name: 'X' }}
                        >
                            Remove image
                        </Button>
                    </Box>
                )}
                {isEditable && !screenshotUrl && (
                    <Box
                        position="absolute"
                        top={0}
                        left={0}
                        width="full"
                        height="full"
                        flex
                        center
                        justifyContent="center"
                    >
                        <Button
                            startIcon={{ name: 'Plus' }}
                            variant="ghost"
                            onClick={onUploadClick}
                            size="s"
                        >
                            Upload image
                        </Button>
                    </Box>
                )}
                {showThumbnail && (
                    <Thumbnail
                        src={screenshotUrl}
                        size="manual"
                        alt={template.name}
                        className={AppTemplateDetailScreenshotStyle}
                    />
                )}
            </Skeleton>
        </Box>
    )
}
